import type { Language } from "@bt/domain";
import { useMutation } from "@tanstack/react-query";
import {
  type LucideIcon,
  MonitorIcon,
  MoonIcon,
  PackageOpenIcon,
  SquareDashedIcon,
  SunIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { LANGUAGE_OPTIONS, setLanguage, toLanguage } from "@/i18n";
import { needsWelcome, safeNext, signInUrl, useMe } from "@/lib/account";
import { errorMessage } from "@/lib/errors";
import { useFormat } from "@/lib/format";
import { useLocation } from "@/lib/pages";
import { type ThemeChoice, useTheme } from "@/lib/theme";
import { useTRPC, useTRPCClient } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { AuthCard } from "./account-app";
import { FormError } from "./sign-in";

/**
 * /account/welcome: the welcome steps of a first sign-in (ADR 0011). Everyone picks a language
 * and a theme, kept with their account; a visitor of the public demo then creates their demo
 * garage, with the example data or empty (ADR 0012). Afterwards it goes on to ?next=.
 */
export function WelcomePage() {
  const { t, i18n } = useTranslation();
  const trpc = useTRPC();
  const client = useTRPCClient();
  const format = useFormat();
  const { search } = useLocation();
  const next = safeNext(search.get("next"));
  const { data: me, isPending } = useMe();
  const { theme: shownTheme, setTheme } = useTheme();
  const finish = useMutation({
    ...trpc.account.finishWelcome.mutationOptions(),
    meta: { silent: true },
  });
  const [step, setStep] = useState<number | null>(null);
  const [language, setChosenLanguage] = useState<Language>(toLanguage(i18n.language));
  const [theme, setChosenTheme] = useState<ThemeChoice>(shownTheme);
  const [exampleData, setExampleData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const done = me ? !needsWelcome(me) : false;
  useEffect(() => {
    if (isPending) return;
    if (!me) window.location.replace(signInUrl("/"));
    else if (me.status !== "active") window.location.replace("/account/restricted");
    else if (done) window.location.replace(me.demo?.garage ? `/${me.demo.garage}/` : next);
  }, [me, isPending, done, next]);
  // A visitor who chose language and theme already comes back to their demo garage's step.
  useEffect(() => {
    if (me && step === null) setStep(me.welcomed ? 2 : 0);
  }, [me, step]);
  if (me?.status !== "active" || done || step === null) return null;

  const visitor = me.demo !== null;
  const steps = visitor ? 3 : 2;

  /** Saves language and theme with the account, once. */
  const saveChoices = async () => {
    if (!me.welcomed) await finish.mutateAsync({ language, theme });
  };
  const run = async (work: () => Promise<void>) => {
    setError(null);
    setPending(true);
    try {
      await work();
    } catch (e) {
      setError(errorMessage(e, t));
      setPending(false);
    }
  };

  let page: ReactNode;
  if (step === 0) {
    page = (
      <Step
        title={t("welcome.languageTitle")}
        body={t("welcome.languageBody")}
        label={t("welcome.step", { step: 1, steps })}
      >
        <Choices<Language>
          label={t("welcome.languageTitle")}
          value={language}
          onChange={(next) => {
            setChosenLanguage(next);
            setLanguage(next);
          }}
          options={LANGUAGE_OPTIONS.map((l) => ({ value: l.code, label: l.label }))}
        />
        <Button size="lg" onClick={() => setStep(1)}>
          {t("welcome.continue")}
        </Button>
      </Step>
    );
  } else if (step === 1) {
    page = (
      <Step
        title={t("welcome.themeTitle")}
        body={t("welcome.themeBody")}
        label={t("welcome.step", { step: 2, steps })}
      >
        <Choices<ThemeChoice>
          label={t("welcome.themeTitle")}
          value={theme}
          onChange={(next) => {
            setChosenTheme(next);
            setTheme(next);
          }}
          options={[
            { value: "system", label: t("settings.themeSystem"), icon: MonitorIcon },
            { value: "light", label: t("settings.themeLight"), icon: SunIcon },
            { value: "dark", label: t("settings.themeDark"), icon: MoonIcon },
          ]}
        />
        <FormError message={error} />
        <div className="flex gap-2">
          <Button variant="outline" size="lg" onClick={() => setStep(0)}>
            {t("welcome.back")}
          </Button>
          {visitor ? (
            <Button size="lg" className="flex-1" onClick={() => setStep(2)}>
              {t("welcome.continue")}
            </Button>
          ) : (
            <Button
              size="lg"
              className="flex-1"
              disabled={pending}
              onClick={() =>
                run(async () => {
                  await saveChoices();
                  window.location.assign(next);
                })
              }
            >
              {t("welcome.finish")}
            </Button>
          )}
        </div>
      </Step>
    );
  } else {
    page = (
      <Step
        title={t("welcome.garageTitle")}
        body={t("welcome.garageBody", {
          date: format.dateTime(me.demo?.expiresAt ?? new Date().toISOString()),
        })}
        label={t("welcome.step", { step: 3, steps })}
      >
        <Choices<"example" | "empty">
          label={t("welcome.garageTitle")}
          value={exampleData ? "example" : "empty"}
          onChange={(next) => setExampleData(next === "example")}
          options={[
            {
              value: "example",
              label: t("welcome.exampleData"),
              description: t("welcome.exampleDataBody"),
              icon: PackageOpenIcon,
            },
            {
              value: "empty",
              label: t("welcome.empty"),
              description: t("welcome.emptyBody"),
              icon: SquareDashedIcon,
            },
          ]}
        />
        <FormError message={error} />
        <div className="flex gap-2">
          {me.welcomed ? null : (
            <Button variant="outline" size="lg" onClick={() => setStep(1)}>
              {t("welcome.back")}
            </Button>
          )}
          <Button
            size="lg"
            className="flex-1"
            disabled={pending}
            onClick={() =>
              run(async () => {
                await saveChoices();
                const { garage } = await client.publicDemo.start.mutate({
                  exampleData,
                  language: me.welcomed ? (me.language ?? language) : language,
                });
                window.location.assign(`/${garage}/`);
              })
            }
          >
            {t("welcome.create")}
          </Button>
        </div>
      </Step>
    );
  }
  return page;
}

function Step({
  title,
  body,
  label,
  children,
}: {
  title: string;
  body: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <AuthCard title={title} body={body}>
      <p className="-mt-2 mb-4 text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {label}
      </p>
      <div className="flex flex-col gap-5">{children}</div>
    </AuthCard>
  );
}

/** Large radio choices, one per line, for the few options of a welcome step. */
function Choices<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string; description?: string; icon?: LucideIcon }[];
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-col gap-2">
      {options.map((option) => {
        const checked = option.value === value;
        const Icon = option.icon;
        return (
          // biome-ignore lint/a11y/useSemanticElements: cards styled as choices, announced as radios
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={checked}
            onClick={() => onChange(option.value)}
            className={cn(
              "flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
              checked
                ? "border-brand bg-brand-soft text-foreground"
                : "border-border bg-surface hover:border-input",
            )}
          >
            {Icon ? (
              <Icon
                className={cn(
                  "mt-0.5 size-5 shrink-0",
                  checked ? "text-brand-fg" : "text-muted-foreground",
                )}
                aria-hidden
              />
            ) : null}
            <span className="flex min-w-0 flex-col">
              <span className="text-sm font-medium">{option.label}</span>
              {option.description ? (
                <span className="text-xs text-pretty text-muted-foreground">
                  {option.description}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
